import { useState, useEffect } from "react";
import { authHeaders } from "../api";

function EquityCurve({ points }) {
  if (!points.length) return (
    <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#2A3040", fontSize: 10 }}>
      Pas encore de trades fermés
    </div>
  );

  const W = 600, H = 140, PAD = 20;
  const equities = points.map(p => p.equity);
  const minE = Math.min(0, ...equities);
  const maxE = Math.max(0, ...equities);
  const range = maxE - minE || 1;

  const toX = i => PAD + (i / (points.length - 1 || 1)) * (W - PAD * 2);
  const toY = v => PAD + (1 - (v - minE) / range) * (H - PAD * 2);

  const pts = points.map((p, i) => `${toX(i)},${toY(p.equity)}`).join(" ");
  const zeroY = toY(0);
  const lastEquity = equities[equities.length - 1] || 0;
  const color = lastEquity >= 0 ? "#00E5C0" : "#E03030";

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 160 }}>
      {/* zero line */}
      <line x1={PAD} y1={zeroY} x2={W - PAD} y2={zeroY}
            stroke="#1C2030" strokeWidth={1} strokeDasharray="4,4" />
      {/* equity curve */}
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} />
      {/* dots at each trade */}
      {points.map((p, i) => (
        <circle key={i} cx={toX(i)} cy={toY(p.equity)} r={3}
                fill={p.pnl_pct >= 0 ? "#00E5C0" : "#E03030"} opacity={0.8} />
      ))}
    </svg>
  );
}

function BreakdownTable({ title, data }) {
  const rows = Object.entries(data || {});
  if (!rows.length) return null;
  return (
    <div style={{ background: "#0D1117", border: "1px solid #1C2030", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
      <div style={{ padding: "10px 16px", background: "#0A0C10", fontSize: 8, color: "#2A3040", letterSpacing: 2 }}>
        {title}
      </div>
      {rows.map(([key, v]) => {
        const winColor = v.win_rate >= 55 ? "#00E5C0" : v.win_rate >= 40 ? "#E5A000" : "#E03030";
        const pnlColor = v.avg_pnl >= 0 ? "#00E5C0" : "#E03030";
        return (
          <div key={key} style={{
            display: "grid", gridTemplateColumns: "1fr 60px 60px 70px 60px",
            gap: 8, padding: "9px 16px", borderBottom: "1px solid #0A0C10",
            alignItems: "center",
          }}>
            <div style={{ fontSize: 10, color: "#E0E4F0", fontWeight: 600 }}>{key}</div>
            <div style={{ fontSize: 9, color: "#555E6E" }}>{v.trades} trades</div>
            <div style={{ fontSize: 10, color: winColor, fontWeight: 700 }}>{v.win_rate}%</div>
            <div style={{ fontSize: 9, color: pnlColor }}>
              moy {v.avg_pnl >= 0 ? "+" : ""}{v.avg_pnl}%
            </div>
            <div style={{ fontSize: 9, color: v.pnl >= 0 ? "#00E5C0" : "#E03030" }}>
              {v.pnl >= 0 ? "+" : ""}${v.pnl}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsView() {
  const [data, setData]   = useState(null);
  const [perf, setPerf]   = useState(null);

  useEffect(() => {
    fetch("/api/analytics", { headers: authHeaders() }).then(r => r.json()).then(setData)
      .catch(() => setData({ points: [], total_trades: 0, win_rate: 0, total_pnl_usd: 0 }));
    fetch("/api/analytics/performance", { headers: authHeaders() }).then(r => r.json()).then(setPerf)
      .catch(() => setPerf(null));
  }, []);

  if (!data) return (
    <div style={{ textAlign: "center", padding: 60, color: "#2A3040", fontSize: 11 }}>
      Chargement...
    </div>
  );

  const pnlColor = data.total_pnl_usd >= 0 ? "#00E5C0" : "#E03030";

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#E0E4F0", marginBottom: 16 }}>
        ANALYTICS
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
        {[
          { label: "TRADES FERMÉS", value: data.total_trades },
          { label: "WIN RATE", value: `${data.win_rate}%` },
          { label: "P&L TOTAL", value: `${data.total_pnl_usd >= 0 ? "+" : ""}$${data.total_pnl_usd}`, color: pnlColor },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: "#0D1117", border: "1px solid #1C2030", borderRadius: 8,
            padding: "14px 16px",
          }}>
            <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: color || "#E0E4F0" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Equity curve */}
      <div style={{ background: "#0D1117", border: "1px solid #1C2030", borderRadius: 8, padding: "16px", marginBottom: 16 }}>
        <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 12 }}>COURBE EQUITY</div>
        <EquityCurve points={data.points} />
      </div>

      {/* Performance breakdown */}
      {perf && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 2, marginBottom: 8 }}>
            PERFORMANCE PAR CATÉGORIE
          </div>
          <BreakdownTable title="PAR SOURCE" data={perf.by_source} />
          <BreakdownTable title="PAR SCORE" data={perf.by_score_range} />
          <BreakdownTable title="PAR RAISON DE VENTE" data={perf.by_sell_reason} />
          <BreakdownTable title="PAR CHAÎNE" data={perf.by_chain} />
        </div>
      )}

      {/* Trade list */}
      {data.points.length > 0 && (
        <div style={{ background: "#0D1117", border: "1px solid #1C2030", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", background: "#0A0C10",
                        fontSize: 8, color: "#2A3040", letterSpacing: 2 }}>
            HISTORIQUE DES TRADES
          </div>
          {[...data.points].reverse().map((p, i) => (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "1fr 80px 70px 70px 1fr",
              gap: 8, padding: "10px 16px", borderBottom: "1px solid #0A0C10",
              alignItems: "center",
            }}>
              <div style={{ fontSize: 10, color: "#E0E4F0", fontWeight: 600 }}>{p.symbol}</div>
              <div style={{ fontSize: 9, color: p.pnl_pct >= 0 ? "#00E5C0" : "#E03030", fontWeight: 700 }}>
                {p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct}%
              </div>
              <div style={{ fontSize: 9, color: p.pnl_usd >= 0 ? "#00E5C0" : "#E03030" }}>
                {p.pnl_usd >= 0 ? "+" : ""}${p.pnl_usd}
              </div>
              <div style={{ fontSize: 9, color: "#555E6E" }}>
                ${p.equity >= 0 ? "+" : ""}{p.equity}
              </div>
              <div style={{ fontSize: 8, color: "#2A3040", textAlign: "right" }}>
                {new Date(p.date).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
